import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Paper,
    Toolbar,
    Typography,
    IconButton,
    Tooltip,
    Checkbox,
    Box,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Done as DoneIcon,
    Clear as ClearIcon,
    FiberManualRecord as HelpIcon,
    ArrowBack,
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import LinkIcon from '@mui/icons-material/Link';
import BlockIcon from '@mui/icons-material/Block';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import { downloadDeliveryNotesPdf, viewDeliveryNote } from '../../Helpers';
import { useSelector } from 'react-redux';
import DateRangePicker from '../../Components/common/DateRangePicker';
import Loader from '../../Layout/Loader';

const dummyData = [
    { id: 1, name: 'Dashboard Chart Widget', check: true },
    { id: 2, name: 'User Analytics Widget', check: false },
    { id: 3, name: 'Notification Feed Widget', check: true },
    { id: 4, name: 'Progress Tracker Widget', check: false },
    { id: 5, name: 'Custom Report Widget', check: true },
];


const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    borderRadius: theme.shape.borderRadius,
}));

const StyledToolbar = styled(Toolbar)(({ theme, selectedCount }) => ({
    ...(selectedCount > 0 && {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.08 : 0.2),
    }),
    minHeight: '48px !important',
    padding: theme.spacing(0, 1),
    marginTop: selectedCount === 0 ? 0 : theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
    whiteSpace: 'nowrap',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: alpha(theme.palette.action.hover, 0.08),
    },
    '& .actionButton': {
        opacity: 0,
        transition: 'opacity 0.2s',
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    '&:hover .actionButton': {
        opacity: 1,
    },
}));


const CustomCheckbox = ({ checked, indeterminate, onChange, disabled, ...props }) => (
    <Checkbox
        checked={checked || false}
        indeterminate={indeterminate || false}
        onChange={onChange}
        disabled={disabled}
        size="small"
        {...props}
    />
);

const invoiceStatusMap = {
    0: "Not Generated",
    1: "Already Generated",
    2: "Returned",
    3: "Excluded",
    4: "Rejected",
};


const EnhancedTable = ({
    rows = dummyData,
    rowDefinition = [{ id: 'name', numeric: false, disablePadding: false, label: 'Name' }],
    title = 'Data Table',
    searchable = false,
    createButton = true,
    onCreate,
    isEditable = false,
    showCheckbox = true,
    showGroupAccess = false,
    canUpdate = false,
    canDelete = false,
    isPOInvoicesDn = false,
    showSearchButton = false,
    submitSearch,
    SearchChange,
    onEdit,
    onDelete,
    onBack,
    isBack = false,
    onSelectionChange,
    page = 1,
    rowsPerPage = 5,
    onPageChange,
    onRowsPerPageChange,
    className = '',
    totalData,
    translate,
    canViewDn,
    isSaleOrderHeader = false,
    canDownloadPdf,
    ...props
}) => {
    const { t } = useTranslation();
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [selected, setSelected] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [selectAllChecked, setSelectAllChecked] = useState(null);
    const logInUser = useSelector((state) => state.logInUsers)
        let serverTimeZoneName = logInUser?.attributes?.timezone || "Asia/Riyadh";


    // Initialize selected from rows' 'check' field
    useEffect(() => {
        const initialSelected = rows.filter(row => row.check).map(row => row.id);
        setSelected(initialSelected);
    }, [rows]);

    // Select All Checkbox Handler
    const handleSelectAllClick = (event) => {
        setSelectAllChecked(event.target.checked);
        setConfirmationModal(true);
    };


    const handleConfirmSelectAll = () => {
        let newSelected = [];
        if (selectAllChecked) {
            newSelected = rows.map((n) => n.id);
        } else {
            newSelected = [];
        }
        setSelected(newSelected);
        if (onSelectionChange) onSelectionChange(newSelected);
        setConfirmationModal(false);
        setSelectAllChecked(null);
    };


    const handleSingleClick = (event, row) => {
        const selectedIndex = selected.indexOf(row.id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, row.id);
        } else {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        setSelected(newSelected);
        if (onSelectionChange) onSelectionChange(newSelected);
    };


    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        if (onPageChange) onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        if (onRowsPerPageChange) onRowsPerPageChange(newRowsPerPage);
    };



    const handleSearchChange = (event) => {
        setSearchValue(event.target.value)
        if (handleSearchChange) {
            SearchChange(event.target.value)
        }
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;


    const searchFilter = (array, value, columns) => {
        if (!value) return array;
        return array.filter((item) =>
            columns.some((col) => {
                const cellValue = item[col.id] || '';
                return cellValue.toString().toLowerCase().includes(value.toLowerCase());
            })
        );
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const getComparator = (order, orderBy) =>
        order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const visibleRows = React.useMemo(
        () =>
            stableSort(
                searchFilter(rows, searchValue, rowDefinition),
                getComparator(order, orderBy)
            ),
        [order, orderBy, rows, searchValue, rowDefinition]
    );


    const numSelected = selected.length;
    const emptyRows = 0;

    const handleEdit = () => onEdit?.(selected);
    const handleDelete = () => {
        onDelete?.(selected);
        setSelected([]);
    };
    const handleCreate = () => onCreate?.();

    return (
        <Paper elevation={0} sx={{ width: '100%', mb: 2, mt: 0 }} className={className}>
            <Paper
                elevation={1}
                sx={{
                    p: 2,
                    mb: 1,
                    borderRadius: 1,
                    border: `1px solid ${alpha('#000', 0.12)}`,
                    backgroundColor: theme => theme.palette.background.paper,
                }}
            >
                {isPOInvoicesDn ?
                    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                        <TextField
                            size="small"
                            label={t('deliveryNote')}
                            value={props.poInvoicesDn?.deliveryNoteNumber}
                            onChange={(event) => props.handlePoInvoicesDn("deliveryNoteNumber", event.target.value)}
                            sx={{ minWidth: 200 }}
                        />
                        <TextField
                            size="small"
                            label={t('deliveryNoteRefNumber')}
                            value={props.poInvoicesDn?.deliveryNoteRefNumber}
                            onChange={(event) => props.handlePoInvoicesDn("deliveryNoteRefNumber", event.target.value)}
                            sx={{ minWidth: 200 }}
                        />
                        <Button variant="contained" onClick={props.handleSearch} color="primary">
                            {t('search')}
                        </Button>
                                    <Button variant="contained" onClick={onBack} color="primary">
                            {t('back')}
                        </Button>
                    </Box> : isSaleOrderHeader ?
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                            <Box display="flex" alignItems="center" gap={0.5} minWidth={200}>
                                <DateRangePicker
                                    maintenanceTop
                                    fill
                                    error={props.timeError || false}
                                    selectedDate={props.saleOrderFilter?.selectedDateTime}
                                    label={t('reportFrom') + ' - ' + t('reportTo')}
                                    onEvent={props.handleDateTime}
                                />
                                <Box width={30} display="flex" justifyContent="center">
                                    {props.saleOrderFilter?.selectedDateTime && (
                                        <IconButton
                                            size="small"
                                            color="inherit"
                                            onClick={props.handleClearSingleOrderFilter}
                                            aria-label="clear date"
                                            title="Clear date"
                                            sx={{ p: 0.5 }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                            <TextField
                                size="small"
                                label={t('search')}
                                value={props.saleOrderFilter?.searchValue}
                                onChange={(event) => props.handleSaleOrderFilter("searchValue", event.target.value)}
                                sx={{ minWidth: 200 }}
                            />
                            <TextField
                                size="small"
                                label={t('plateNumber')}
                                value={props.saleOrderFilter?.searchPlateNumber}
                                onChange={(event) => props.handleSaleOrderFilter("searchPlateNumber", event.target.value)}
                                sx={{ minWidth: 200 }}
                            />
                            <Button variant="contained" onClick={props.handleSearch} color="primary">
                                {t('search')}
                            </Button>
                        </Box>

                        :
                        <Box display="flex" justifyContent="space-between">
                            <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    color: 'text.primary',
                                }}
                            >
                                {title} {numSelected > 0 && `(${numSelected} selected)`}
                            </Typography>
                            <Box display={"flex"} gap={2} alignItems={"center"}>
                                {searchable && (
                                    <TextField
                                        size="small"
                                        label={t('search')}
                                        value={searchValue}
                                        onChange={handleSearchChange}
                                        sx={{ minWidth: 200 }}
                                    />
                                )}

                                {showSearchButton && <Button variant="contained" startIcon={<ArrowBack />} onClick={submitSearch} color="primary">
                                    {t('search')}
                                </Button>}


                                {isBack && (
                                    <Button variant="contained" startIcon={<ArrowBack />} onClick={onBack} color="primary">
                                        {t('back')}
                                    </Button>
                                )}
                            </Box>
                        </Box>}
            </Paper>

            <StyledTableContainer>
                {props.isLoading ? <Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"50vh"}>

                    <Loader />
                </Box>
                    :
                    <Table stickyHeader aria-label="enhanced table">
                        <TableHead>
                            <TableRow>
                                {showCheckbox && (
                                    <StyledTableCell padding="checkbox">
                                        <CustomCheckbox
                                            indeterminate={numSelected > 0 && numSelected < rows.length}
                                            checked={rows.length > 0 && numSelected === rows.length}
                                            onChange={handleSelectAllClick}
                                        />
                                    </StyledTableCell>
                                )}
                                {showGroupAccess && <StyledTableCell padding="checkbox">{t('groupAccess')}</StyledTableCell>}
                                {rowDefinition.map((headCell) => (
                                    <StyledTableCell
                                        key={headCell.id}
                                        align={headCell.numeric ? 'right' : 'left'}
                                        padding={headCell.disablePadding ? 'none' : 'normal'}
                                        sortDirection={orderBy === headCell.id ? order : false}
                                    >
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(headCell.id)}
                                        >
                                            {translate(headCell.label)}
                                        </TableSortLabel>
                                    </StyledTableCell>
                                ))}
                                {isEditable && <StyledTableCell align="right">{t('Actions')}</StyledTableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows?.length === 0 && (
                                <StyledTableRow>
                                    <StyledTableCell
                                        colSpan={
                                            rowDefinition?.length +
                                            (showCheckbox ? 1 : 0) +
                                            (showGroupAccess ? 1 : 0) +
                                            (isEditable ? 1 : 0)
                                        }
                                        align="center"
                                        style={{ padding: "20px", fontSize: "14px", color: "#888" }}
                                    >
                                        No Data Found
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}

                            {visibleRows.map((row) => {
                                const isItemSelected = isSelected(row.id);
                                return (
                                    <StyledTableRow
                                        hover
                                        onClick={(event) => !isEditable && handleSingleClick(event, row)}
                                        role={showCheckbox ? 'checkbox' : undefined}
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        selected={isItemSelected}
                                    >
                                        {showCheckbox && (
                                            <StyledTableCell padding="checkbox">
                                                <CustomCheckbox
                                                    checked={row.check || isItemSelected}
                                                    onChange={(event) => handleSingleClick(event, row)}
                                                />
                                            </StyledTableCell>
                                        )}
                                        {showGroupAccess && (
                                            <StyledTableCell padding="checkbox">
                                                {row.check || row.indeterminate ? (
                                                    row.groupAccess ? <DoneIcon color="success" /> : <ClearIcon color="error" />
                                                ) : null}
                                            </StyledTableCell>
                                        )}
                                        {rowDefinition.map((column) => {
                                            let value;
                                            if (column.id === "creationMode") {
                                                value = row[column.id] === "garageUpload" ? "Normal" : "Mannual";
                                            } else if (column.id === "serviceDate") {
                                                value = row[column.id]
                                                    ? moment.utc(row[column.id])
                                                        .tz(serverTimeZoneName)
                                                        .format("DD-MM-YYYY")
                                                    : "-";
                                            } else if (column.id === "invoiceMade") {
                                                value = invoiceStatusMap[row[column.id]] || "";
                                            } else if (column.id === "attributes.saleTotal") {
                                                value = row?.attributes?.saleTotal || 0;
                                            } else if (column.id === "attributes.saleNet") {
                                                value = row?.attributes?.saleNet || 0;
                                            } else if (column.id === "attributes.saleVat") {
                                                value = row?.attributes?.saleVat || 0;
                                            }
                                            else {
                                                value = row[column.id] || "-";
                                            }

                                            return (
                                                <StyledTableCell
                                                    key={column.id}
                                                    align={column.numeric ? "right" : "left"}
                                                >
                                                    {value}
                                                </StyledTableCell>
                                            );
                                        })}
                                        {isEditable && (
                                            <StyledTableCell align="right">
                                                {canUpdate && (
                                                    <Tooltip title={t('edit')}>
                                                        <IconButton
                                                            className="actionButton"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onEdit?.([row]);
                                                            }}
                                                            color="primary"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title={t('delete')}>
                                                        <IconButton
                                                            className="actionButton"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDelete?.([row]);
                                                            }}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {canDelete && (
                                                    <Tooltip title={t('delete')}>
                                                        <IconButton
                                                            className="actionButton"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDelete?.([row]);
                                                            }}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {canViewDn && (
                                                    <Tooltip title={t('viewDeliveryNote')}>
                                                        <IconButton
                                                            className="actionButton"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewDeliveryNote(row?.deliveryNoteId, logInUser)
                                                                // n.invoiceId !== 0 ? (e) => this.viewDeliveryNote(e, [n], 'history') : null
                                                                // onDelete?.([row]);
                                                            }}
                                                            color="inherit"
                                                        >
                                                            {[row]?.deliveryNoteId !== 0 ? (
                                                                <LinkIcon className="actionButton" />
                                                            ) : (
                                                                <BlockIcon titleAccess="Cannot View" sx={{ color: 'gray', fontSize: 16 }} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDownloadPdf &&
                                                    (
                                                        <Tooltip title={t('downloadPdf')}>
                                                            <IconButton
                                                                className="actionButton"
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    downloadDeliveryNotesPdf([row], props.dispatch)
                                                                    // onDownloadPdf?.([row])
                                                                }}
                                                                color="inherit"
                                                            >
                                                                <PictureAsPdf className="actionButton" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )
                                                }
                                            </StyledTableCell>
                                        )}
                                    </StyledTableRow>
                                );
                            })}
                            {emptyRows > 0 && (
                                <StyledTableRow style={{ height: 53 * emptyRows }}>
                                    <StyledTableCell colSpan={6} />
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                }
            </StyledTableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10,20, 25, 50, 500]}
                component="div"
                count={totalData}
                rowsPerPage={rowsPerPage}
                page={page - 1} // convert 1-based → 0-based
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('rowsPerPage')}
            />

            <Dialog open={confirmationModal} onClose={() => setConfirmationModal(false)}>
                <DialogTitle>{t('confirmAction')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {selectAllChecked
                            ? t('Are You Sure to Select All', { count: rows.length })
                            : t('Are You Sure to DeSelect All', { count: rows.length })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmationModal(false)}>{t('cancel')}</Button>
                    <Button onClick={handleConfirmSelectAll} autoFocus>{t('confirm')}</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default EnhancedTable;